/**
 * Modal Stack Manager
 *
 * Coordinates Escape-to-close behavior across stacked Modal instances.
 *
 * Why this exists:
 * - Each open Modal pushes its onClose onto a shared stack.
 * - A SINGLE `keydown` listener is attached to `document` ONLY while the
 *   stack is non-empty. When the last modal unregisters, the listener is
 *   removed. This avoids the "module-scope addEventListener" anti-pattern
 *   that registers a listener at import time (which can leak across HMR
 *   reloads / test re-imports and is invisible to React's lifecycle).
 * - Escape closes only the topmost (last-pushed) modal — stacked modals
 *   do not cascade-close on a single key press.
 */
import { logger } from '@/shared/utils/logger'

type ModalCloseHandler = () => void
type ModalStackListener = () => void

/** How a dialog may be dismissed right now; read lazily at the moment of the key press / click. */
export interface ModalDismissPolicy {
  /** A save/delete is in flight: Escape, backdrop and ✕ do nothing. */
  busy?: boolean
  /** `false` turns dismissal into `onDismissAttempt` (e.g. "Discard changes?") instead of closing. */
  dismissible?: boolean
  onDismissAttempt?: () => void
  testId?: string
  /**
   * Shared flag owned by the dialog: the «dismiss blocked» warning is emitted once per busy
   * attempt, not on every repeated Escape / click. The dialog resets it when `busy` ends.
   */
  busyWarned?: { current: boolean }
}

type ModalPolicyReader = () => ModalDismissPolicy

interface ModalEntry {
  onClose: ModalCloseHandler
  getPolicy: ModalPolicyReader
  getPanel: () => Element | null
}

const DEFAULT_POLICY_READER: ModalPolicyReader = () => ({})

const modalCloseStack: ModalEntry[] = []
/**
 * Quien necesita saber si HAY un diálogo abierto. Nació para una capa fija: la
 * barra de acciones del importador personalizado va por encima del panel de
 * progreso (z-60 sobre z-50) y por eso tapaba también los botones de los
 * diálogos, que son z-50 — ver `import/constants/overlay-layers.ts`.
 */
const modalStackListeners = new Set<ModalStackListener>()
let documentKeydownListener: ((event: KeyboardEvent) => void) | null = null

function notifyModalStackListeners(): void {
  for (const listener of modalStackListeners) listener()
}

/**
 * One decision for every way of dismissing a dialog (Escape, backdrop, ✕):
 * busy blocks all three; a non-dismissible dialog reports the attempt instead
 * of closing; otherwise it closes.
 */
export function requestModalDismiss(onClose: ModalCloseHandler, policy: ModalDismissPolicy): void {
  if (policy.busy === true) {
    if (policy.busyWarned?.current !== true) {
      if (policy.busyWarned) policy.busyWarned.current = true
      logger.warn('modal.dismiss-blocked', { testId: policy.testId, reason: 'busy' })
    }
    return
  }
  if (policy.dismissible === false) {
    policy.onDismissAttempt?.()
    return
  }
  onClose()
}

function handleEscapeKeyForTopModal(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || modalCloseStack.length === 0) return
  const topMost = modalCloseStack[modalCloseStack.length - 1]
  // The topmost dialog handles Escape (closes, is busy or is not dismissible): nobody else should.
  event.preventDefault()
  requestModalDismiss(topMost.onClose, topMost.getPolicy())
}

function attachDocumentKeydownListenerIfNeeded(): void {
  if (typeof document === 'undefined') return
  if (documentKeydownListener !== null) return
  documentKeydownListener = handleEscapeKeyForTopModal
  document.addEventListener('keydown', documentKeydownListener)
}

function detachDocumentKeydownListenerIfStackEmpty(): void {
  if (typeof document === 'undefined') return
  if (modalCloseStack.length > 0) return
  if (documentKeydownListener === null) return
  document.removeEventListener('keydown', documentKeydownListener)
  documentKeydownListener = null
}

/**
 * Register a modal's onClose handler at the top of the stack.
 * Returns an unregister function that MUST be called on unmount/close.
 */
export function registerModalOnCloseHandler(
  onClose: ModalCloseHandler,
  getPolicy: ModalPolicyReader = DEFAULT_POLICY_READER,
  getPanel: () => Element | null = () => null,
): () => void {
  const entry: ModalEntry = { onClose, getPolicy, getPanel }
  modalCloseStack.push(entry)
  attachDocumentKeydownListenerIfNeeded()
  notifyModalStackListeners()

  return function unregisterModalOnCloseHandler(): void {
    const indexInStack = modalCloseStack.lastIndexOf(entry)
    if (indexInStack !== -1) modalCloseStack.splice(indexInStack, 1)
    detachDocumentKeydownListenerIfStackEmpty()
    notifyModalStackListeners()
  }
}

/**
 * True when `panel` is the dialog panel of the topmost registered modal. Keyboard shortcuts that
 * live on `document` (Enter-to-confirm) use it so a dialog under another one never reacts.
 */
export function isTopmostModalPanel(panel: Element | null): boolean {
  if (panel === null || modalCloseStack.length === 0) return false
  return modalCloseStack[modalCloseStack.length - 1].getPanel() === panel
}

/** Returns true while at least one modal is registered (i.e. open). */
export function isAnyModalOpen(): boolean {
  return modalCloseStack.length > 0
}

/**
 * Avisa cuando la pila de modales cambia. Pensado para `useSyncExternalStore`:
 * devuelve la función de baja, y el estado se lee con {@link isAnyModalOpen}.
 */
export function subscribeToModalStack(listener: ModalStackListener): () => void {
  modalStackListeners.add(listener)

  return function unsubscribeFromModalStack(): void {
    modalStackListeners.delete(listener)
  }
}
